import { onCallMonarchWithPreampCallback, getAutoMonarchExperienceReturnValue } from '../components/Cohesion/AutoMonarchCall.js';
import { requestMonarch } from './monarch.js';
import { logCustomAttributes, logEvent, logError } from './logging.js';
import { debugLog } from './debugLog.js';

const cohesionReady = () => {
  const {
    anonymousId = 'null',
    sessionId = 'null',
    instanceId = 'null'
  } = window?._Cohesion || {};
  const attributes = [{
    name: 'cohesion.anonymousId',
    value: anonymousId
  }, {
    name: 'cohesion.sessionId',
    value: sessionId
  }, {
    name: 'cohesion.instanceId',
    value: instanceId
  }, {
    name: 'enduser.id',
    value: anonymousId
  }];
  debugLog('cohesion:ready', attributes);
  logCustomAttributes(attributes);
  logEvent('cohesion:ready');
};
const cohesionCatch = error => {
  console.error('cohesion:catch:', error);
  debugLog('cohesion:catch:', error);
  logError(`cohesion:catch: ${error}`);
  logEvent('cohesion:catch');
};
const preampDecisionsReceived = (setPreampDecisions, setMonarchResponse, callMonarchWithPreamp) => {
  return decisions => {
    debugLog('preamp:decisionsReceived', decisions);
    let audienceName = decisions?.audienceName || '';
    let experienceName = decisions?.experienceName || '';
    let experienceNumber = decisions?.experienceNumber || '';
    if (decisions.preview) {
      audienceName = '';
      experienceName = 'Preview';
      experienceNumber = Number(window._Preamp.preview.split('=')[1].split(':')[0]);
    }

    // Send attributes to New Relic
    logCustomAttributes([{
      name: 'cohesion.audience',
      value: audienceName
    }, {
      name: 'cohesion.decisionId',
      value: decisions.decisionId
    }, {
      name: 'cohesion.experienceName',
      value: experienceName
    }, {
      name: 'cohesion.experience',
      value: `${experienceNumber}`
    }, {
      name: 'cohesion.testName',
      value: decisions?.testName || ''
    },
    // Deprecate the attributes below after business have updated their queries to the standardized events.
    {
      name: 'preamp.audience',
      value: audienceName
    }, {
      name: 'preamp.decisionId',
      value: decisions.decisionId
    }, {
      name: 'preamp.experienceName',
      value: experienceName
    }, {
      name: 'preamp.experienceNumber',
      value: `${experienceNumber}`
    }, {
      name: 'preamp.testName',
      value: decisions?.testName || ''
    }]);
    logEvent('preamp:decisionsReceived', {
      placements: decisions?.placements?.length
    });

    // Format data into a hashtable
    const preampDecisions = decisions.placements.reduce((acc, placement) => ({
      ...acc,
      [placement.placementId]: placement
    }), {});
    debugLog('preamp:decisionsReceived - setting preampDecisions state', preampDecisions);
    setPreampDecisions(preampDecisions);
    if (typeof preampDecisions === 'object') {
      const callMonarch = callMonarchWithPreamp(preampDecisions);
      if (callMonarch) {
        const {
          rulesetId,
          payload,
          monarchParams = {}
        } = callMonarch;
        debugLog('callMonarchWithPreamp callback returning true, calling Monarch now with:', {
          rulesetId,
          payload
        });
        requestMonarch({
          method: 'ruleset',
          id: rulesetId,
          payload,
          params: monarchParams,
          callback: onCallMonarchWithPreampCallback(setMonarchResponse)
        });
      }
    }
    return preampDecisions;
  };
};

// Monarch 'done' is fired after the Monarch native auto call is finished and preamp is done
const monarchDone = setMonarchAutoCallResponse => {
  return (err, autoMonarchResponse) => {
    if (err) {
      debugLog('autoMonarch:error', err);
      const errString = JSON.stringify(err);
      logError(`autoMonarch:error ${errString}`);
      logEvent('autoMonarch:error', {
        error: errString
      });
      setMonarchAutoCallResponse(true);
    } else {
      debugLog('autoMonarch:done - no error', autoMonarchResponse);
      const autoMonarchExperience = getAutoMonarchExperienceReturnValue(autoMonarchResponse ?? {});
      if (autoMonarchExperience) {
        const {
          name,
          experienceNumber
        } = autoMonarchExperience;
        logCustomAttributes([{
          name: `autoMonarch.experienceName`,
          value: name
        }, {
          name: `autoMonarch.experienceNumber`,
          value: experienceNumber
        }]);
        debugLog(`autoMonarch:done - experience present #${experienceNumber} '${name}'`);
      }
      logEvent('autoMonarch:done');
      debugLog('autoMonarch:done - setting state');
      setMonarchAutoCallResponse(autoMonarchResponse);
    }
  };
};
const preampError = setPreampDecisions => {
  return error => {
    console.error('Bricks | preamp:error', error);
    const errString = JSON.stringify(error);
    logError(`preamp:error: ${errString}`);
    logEvent('preamp:error', {
      error: errString
    });
    debugLog('preamp:error', error);
    setPreampDecisions(true);
    return true;
  };
};
const tagularReady = () => {
  logEvent('tagular:ready');
  debugLog('tagular:ready');
  return true;
};

// context:created - fired on a tagular pageview, before the actual event is fired,
// and this event triggers calls for preamp decisions and monarch evaluations
const contextCreated = (setMonarchAutoCallResponse, setMonarchResponse, setPreampDecisions, setRenderedDecisions) => {
  return () => {
    debugLog('context:created - resetting all state');
    setPreampDecisions(undefined);
    setMonarchResponse(undefined);
    setMonarchAutoCallResponse(undefined);
    setRenderedDecisions(undefined);
  };
};
const fuseLeaseRecieved = setFuseNumber => {
  return leases => {
    debugLog('fuse:leasesReceived', leases);
    setFuseNumber(leases?.[0]?.dnis);
    logEvent('fuse:leasesReceived');
    return leases?.[0]?.dnis;
  };
};
const fuseError = (setFuseNumber, defaultFuseNumber) => {
  return () => {
    setFuseNumber(defaultFuseNumber);
    debugLog('fuse:error');
    logError(new Error('Fuse failed to load'));
    logEvent('fuse:error');
    return true;
  };
};

export { cohesionCatch, cohesionReady, contextCreated, fuseError, fuseLeaseRecieved, monarchDone, preampDecisionsReceived, preampError, tagularReady };
